import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { systemAPI } from "../services/api";

/**
 * ProtectedRoute Component
 * Protects routes from unauthorized access
 * Redirects to login if no valid token is found
 *
 * Features:
 * - Checks for JWT token in localStorage
 * - Validates token expiration
 * - Redirects to login page if authentication fails
 */
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [maintenance, setMaintenance] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (_) {
      return {};
    }
  }, []);

  useEffect(() => {
    if (!token) {
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      setIsTokenValid(false);
      setCheckingSystem(false);
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = tokenPayload.exp * 1000;
      if (Date.now() >= expirationTime) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        setIsTokenValid(false);
        setCheckingSystem(false);
        return;
      }
    } catch (_) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      setIsTokenValid(false);
      setCheckingSystem(false);
      return;
    }

    let mounted = true;

    const checkSystemStatus = async () => {
      try {
        const status = await systemAPI.getSystemStatus();
        const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";
        if (mounted) {
          setMaintenance(Boolean(status?.maintenanceMode) && !isAdmin);
        }
      } catch (_) {
        if (mounted) setMaintenance(false);
      } finally {
        if (mounted) setCheckingSystem(false);
      }
    };

    checkSystemStatus();
    return () => {
      mounted = false;
    };
  }, [currentUser?.role, token]);

  if (!isTokenValid) {
    return <Navigate to="/login" replace />;
  }

  if (checkingSystem) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Loading Aiarbix
          </h2>
          <p className="text-primary/80 mt-2">Please wait...</p>
        </div>
      </div>
    );
  }

  if (maintenance) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center px-4">
        <div className="w-full max-w-2xl rounded-2xl border border-primary/30 bg-gradient-to-br from-[#141414] to-[#090909] p-8 shadow-2xl shadow-[#22c55e]/10">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white flex items-center justify-center font-bold text-xl">
              !
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Maintenance Mode On
            </h1>
          </div>
          <p className="text-primary text-lg mb-3">
            System maintenance is currently active by admin.
          </p>
          <p className="text-white/80 mb-6">
            Please try again after 24 hours. We are upgrading services for better performance and security.
          </p>
          <div className="rounded-xl border border-primary/20 bg-[#22c55e]/10 p-4 text-sm text-primary">
            Need urgent help? Contact support or your admin team for updates.
          </div>
        </div>
      </div>
    );
  }

  // Token exists and is valid, render the protected component
  return children;
};

export default ProtectedRoute;
